import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ArrowUpRightIcon, type Icon} from '@primer/octicons-react'
import {Suspense, type ReactNode} from 'react'
import {MiniStatusSkeletonItem} from './MiniMergeStatusLoading'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {To} from '@github-ui/react-router'

type MiniMergeStatusBoundaryProps = {
  action?: (returnFocusTo?: HTMLElement | null) => void
  to?: To
  actionLabel: string
  boundaryName: string
  children: ReactNode
  text: string
  /** Icon shown on the trailing side of the loading skeleton. Defaults to ArrowUpRightIcon. */
  icon?: Icon
}

export function MiniMergeStatusBoundary({
  action,
  actionLabel,
  boundaryName,
  children,
  text,
  icon,
}: MiniMergeStatusBoundaryProps) {
  return (
    <ErrorBoundary
      boundaryName={boundaryName}
      fallback={<MiniMergeStatusErrorItem action={action} actionLabel={actionLabel} text={text} />}
    >
      <Suspense fallback={<MiniStatusSkeletonItem text={text} action={action} icon={icon} />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function MiniMergeStatusErrorItem({
  action,
  actionLabel,
  text,
  to,
}: Omit<MiniMergeStatusBoundaryProps, 'boundaryName' | 'children' | 'icon'>) {
  const unavailableText = `${text} unavailable`

  return (
    <MiniMergeStatusItem aria-label={actionLabel} onClick={event => action?.(event.currentTarget)} to={to}>
      <MiniMergeStatusItem.LabelRow icon={ArrowUpRightIcon}>
        <MiniMergeStatusItem.Label icon="error">{unavailableText}</MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={unavailableText}
        isUnavailable={false}
        successful={0}
        total={0}
        unavailableLabel={unavailableText}
      />
    </MiniMergeStatusItem>
  )
}
